import FF from "@/components/forms/FormField.module.css";

import { FormField } from "@/components/forms/FormField.tsx";
import { MonthlyCostElement, MonthlyCostField } from "@/components/forms/MonthlyCostField.tsx";
import { WRadioGroup } from "@/components/forms/WRadioGroup.tsx";
import { CurrencyMonthly, CurrencyPerPeriod } from "@/components/l10n/Currency.tsx";
import { NoticeBlock } from "@/components/NoticeBlock.tsx";
import { TODO } from "@/dev/Todo.tsx";
import { TopUpNowButton } from "@/modules/billing/ActionButtons.tsx";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import type { MetalBillingPeriod } from "@/modules/metal/offer/period.tsx";
import { periodLabels, periodUnits, t_billingCyclesDescription } from "@/modules/metal/offer/period.tsx";
import type { MetalOffer, MetalOffersState, OfferEligibility } from "@/modules/metal/offer/useMetalOffers.ts";
import { findOffer } from "@/modules/metal/offer/useMetalOffers.ts";
import type { Price } from "@/modules/pricing/resourcePricing.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Radio } from "react-aria-components";

type OfferVariant = "form" | "modal";

interface MetalOfferSectionProps<TPeriod extends MetalBillingPeriod> extends MetalOffersState<TPeriod> {
    variant: OfferVariant;
    value: TPeriod | null | undefined;
    onChange: (value: TPeriod) => void;
    errorMessage?: ReactNode;
}

/** Billing cycle selection together with its cost summary and billing account warnings. */
export function MetalOfferSection<TPeriod extends MetalBillingPeriod>({
    variant,
    specOffers,
    account,
    location,
    hourlyPrice,
    getEligibility,
    value,
    onChange,
    errorMessage,
}: MetalOfferSectionProps<TPeriod>) {
    const offer = findOffer(specOffers, value);

    return (
        <>
            {specOffers.length ? (
                <OfferRadioGroup
                    variant={variant}
                    offers={specOffers}
                    value={value}
                    errorMessage={errorMessage}
                    onChange={onChange}
                />
            ) : (
                // NB: Unreachable from the create form, where an hourly offer is always available
                <p className="color-muted">
                    <Trans>No reservation billing cycles are available for this server.</Trans>
                </p>
            )}

            {!!offer && (
                <OfferCostField
                    offer={offer}
                    location={location}
                    hourlyPrice={hourlyPrice}
                    billingAccountId={account?.id}
                />
            )}

            <OfferEligibilityNotice variant={variant} account={account} eligibility={getEligibility(value)} />
        </>
    );
}

interface OfferRadioGroupProps<TPeriod extends MetalBillingPeriod> {
    variant: OfferVariant;
    offers: readonly MetalOffer<TPeriod>[];
    value?: TPeriod | null;
    onChange: (value: TPeriod) => void;
    errorMessage?: ReactNode;
}

function OfferRadioGroup<TPeriod extends MetalBillingPeriod>({
    variant,
    offers,
    value,
    onChange,
    errorMessage,
}: OfferRadioGroupProps<TPeriod>) {
    const { t } = useLingui();

    function handleChange(selectedValue: string) {
        const offer = offers.find((item) => String(item.period_hours) === selectedValue);
        if (offer) onChange(offer.period_hours);
    }

    return (
        <WRadioGroup
            wide={variant === "form"}
            block={variant === "modal"}
            headless={variant === "modal"}
            label={t`Billing cycle`}
            isRequired
            value={value == null ? null : String(value)}
            errorMessage={errorMessage}
            description={t_billingCyclesDescription}
            onChange={handleChange}
        >
            {offers.map((item) => (
                <OfferRadio
                    key={item.period_hours}
                    value={item.period_hours}
                    title={periodLabels[item.period_hours]}
                    {...item}
                />
            ))}
        </WRadioGroup>
    );
}

interface OfferRadioProps {
    value: MetalBillingPeriod;
    title: ReactNode;
    price: number;
    billingAccountId?: number;
}

function OfferRadio({ value, title, price, billingAccountId }: OfferRadioProps) {
    const hourlyPrice = value ? price / value : price;
    const isLargerThanMonth = value > 730;

    return (
        <Radio value={String(value)} className={cn(FF.FormFieldIconRadio, FF.OfferRadio)}>
            <div className={FF.Header}>
                <b>{title}</b>
            </div>

            <div className={FF.Footer}>
                {isLargerThanMonth ? (
                    <>
                        <div>
                            <span className="font-size-title">
                                <CurrencyMonthly value={hourlyPrice} />
                            </span>{" "}
                            <span className="text-nowrap">
                                / <Trans>month</Trans>
                            </span>
                        </div>
                        <CurrencyPerPeriod value={price} /> / {periodUnits[value]}
                    </>
                ) : (
                    <MonthlyCostElement estimated price={{ hourly: hourlyPrice }} billingAccountId={billingAccountId} />
                )}
            </div>
        </Radio>
    );
}

interface OfferCostFieldProps {
    offer: MetalOffer;
    location: string;
    /** Price of the hourly (non-reservation) offer, needed only when such an offer can be selected */
    hourlyPrice: Price | undefined;
    billingAccountId?: number;
}

function OfferCostField({ offer, location, hourlyPrice, billingAccountId }: OfferCostFieldProps) {
    const { period_hours, price } = offer;

    if (!period_hours) {
        return hourlyPrice ? (
            <MonthlyCostField price={hourlyPrice} defaultLocation={location} billingAccountId={billingAccountId} />
        ) : null;
    }

    return (
        <FormField
            label={<Trans comment="Field label for reservation upfront cost">Amount billed upfront</Trans>}
            description={
                <Trans>
                    You commit to the full price for the selected period as soon as you confirm the reservation.
                </Trans>
            }
        >
            <span className="font-size-title">
                <CurrencyPerPeriod value={price} />
            </span>{" "}
            <span className="text-nowrap">/ {periodUnits[period_hours]}</span>
            <TODO>Show discounted price</TODO>
        </FormField>
    );
}

interface OfferEligibilityNoticeProps {
    variant: OfferVariant;
    account: EBillingAccount | null;
    eligibility: OfferEligibility;
}

function OfferEligibilityNotice({ variant, account, eligibility }: OfferEligibilityNoticeProps) {
    const { isBlockedForLimited, hasNotEnoughCredit } = eligibility;

    const notice = isBlockedForLimited ? (
        <NoticeBlock color="primary" icon="jp-warning-icon">
            <Trans>The selected billing account is limited and can not create reservations.</Trans>
        </NoticeBlock>
    ) : hasNotEnoughCredit && account ? (
        <NoticeBlock
            color="error"
            icon="jp-warning-icon"
            // TODO: This should calculate a suggested top up amount
            button={<TopUpNowButton account={account} />}
        >
            <Trans>The selected billing account does not have enough credit to purchase this reservation.</Trans>
        </NoticeBlock>
    ) : null;

    if (notice == null) {
        return null;
    }

    return variant === "form" ? <FormField wide>{notice}</FormField> : notice;
}
